import { applyProposalUserAction, updateInitiateFormAnswers } from 'api/operations/enrollmentService';
import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { formUtils } from '@insurancemenu/imenu-components-lib';
import { FormObjectFromJSON } from '@insurancemenu/imenu-components-lib/dist/types';
import { getCart } from 'api/operations/cartServiceV2';
import { getConfigByDate } from 'api/operations/domainServiceV2';
import { INITIAL_FORM_OBJECT, requestFormById } from 'api/operations/formService';
import { ChecklistCompletionStatus, Proposal, RFQChecklistCard, USER_ACTIONS } from 'types/enrollmentService';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import ModalDelegate from 'store/general/modalDelegate';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';

export default class RFQDetailsPageStore {
  rootStore: RootStore;

  private _isLoading = false;
  private _groupId = '';
  private _cartName = '';
  private _form: FormObjectFromJSON;
  private _dynamicForm: null | formUtils.DynamicFormController;
  private _isStepCompleted = false;
  private _createExitModalDelegate = new ModalDelegate();
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get groupId(): string {
    return this._groupId;
  }

  public get form(): FormObjectFromJSON {
    return this._form;
  }

  public get dynamicForm(): null | formUtils.DynamicFormController {
    return this._dynamicForm;
  }

  public get isStepCompleted(): boolean {
    return this._isStepCompleted;
  }

  public get isExitModalVisible(): boolean {
    return this._createExitModalDelegate.isVisible;
  }

  // Setters
  private setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public openExitModal(): void {
    this._createExitModalDelegate.open();
  }

  public closeExitModal(): void {
    this._createExitModalDelegate.close();
  }

  // API
  public async getInitialData(periodId: string, cartId: string): Promise<void> {
    this.setIsLoading(true);

    const cart = await getCart(cartId);
    await runInAction(async () => {
      if (!cart) throw new Error(`Cart with such id doesn't exist: ${cartId}`);
      this._groupId = cart.groupId;
      this._cartName = cart.cartName;
      await this._enrollmentPeriodModule.uploadEnrollmentPeriod(periodId);

      await runInAction(async () => {
        const enrollmentPeriod = this._enrollmentPeriodModule.enrollmentCoreData;
        const config = await getConfigByDate(
          enrollmentPeriod?.sponsorId || '',
          cart.lastUpdateBrowsingCriteria.effectiveDate,
        );
        await runInAction(async () => {
          const formId = config[cart.rateSegment]?.rfqFormId;
          if (formId) {
            await this.getRFQForm(formId, this._enrollmentPeriodModule.getProposalById(cartId));
          }

          runInAction(() => {
            this._isStepCompleted =
              this._enrollmentPeriodModule.getRFQStepCompletionStatus(cartId, RFQChecklistCard.RFQ_DETAILS) ===
              ChecklistCompletionStatus.COMPLETED;

            this.setIsLoading(false);
          });
        });
      });
    });
  }

  private async getRFQForm(formId: string, proposal: Proposal): Promise<void> {
    this._dynamicForm = null;
    this._form = INITIAL_FORM_OBJECT;
    const formRes = await requestFormById(formId);
    runInAction(() => {
      this._form = formRes;
      const initiateFormAnswers = proposal?.initiateFormAnswers || {};
      this.createFormController({ proposalName: this._cartName, ...initiateFormAnswers });
    });
  }

  private createFormController(answers: Record<string, string>): void {
    this._dynamicForm = new formUtils.DynamicFormController(this._form, this.rootStore.userStore.userRoles);

    this._dynamicForm.populateFields(answers);
  }

  private async updateProposalAnswers(periodId: string, cartId: string): Promise<void> {
    const formAnswers = this._dynamicForm?.getFormDataObject() as Record<string, string>;
    await updateInitiateFormAnswers(periodId, cartId, formAnswers);
  }

  public async completeStep(periodId: string, cartId: string): Promise<void> {
    this.setIsLoading(true);
    await this.updateProposalAnswers(periodId, cartId);

    await runInAction(async () => {
      await applyProposalUserAction(periodId, cartId, USER_ACTIONS.COMPLETE_RFQ_DETAILS);
      this.setIsLoading(false);
    });
  }

  public async saveAnswersBeforeExit(periodId: string, cartId: string): Promise<void> {
    this.closeExitModal();

    this.setIsLoading(true);
    await this.updateProposalAnswers(periodId, cartId);
    runInAction(() => {
      this.setIsLoading(false);
    });
  }

  constructor() {
    this.rootStore = getRootStoreInstance();

    this._dynamicForm = null;
    this._form = INITIAL_FORM_OBJECT;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const PageStoreContext = createContext(new RFQDetailsPageStore());

export const useRFQDetailsPageStore = (): RFQDetailsPageStore => {
  return useContext(PageStoreContext);
};

export { PageStoreContext };
